// Tiempo real (D71): una conexion WebSocket con el servicio /rt. Trae la presencia del equipo, los mensajes del chat y
// "escribiendo…" al instante, y le cuenta al equipo el estado de esta persona. Se reconecta sola (espera creciente) y
// si se pierde, la Consola sigue funcionando como antes (consultas cada minuto).
import { useSyncExternalStore } from 'react';

import { getSession, onSessionChange } from '../api/http';

export type EstadoPresencia = 'disponible' | 'ocupado' | 'ausente' | 'dnd' | 'pausa' | 'en-llamada';
export type Presente = { miembroId: string; nombre: string; estado: EstadoPresencia; detalle: string };
export type MensajeChat = { id: string; name: string; canal: string; autorId: string; autorNombre?: string; menciones: string[] | null; titulo: string | null; createdAt: string };
type Evento = { tipo: 'chat'; mensaje: MensajeChat } | { tipo: 'escribiendo'; canal: string; miembroId: string; nombre: string } | { tipo: string; [k: string]: unknown };

type Estado = { conectado: boolean; yo: string | null; presencia: Presente[] };

let estado: Estado = { conectado: false, yo: null, presencia: [] };
const oyentes = new Set<() => void>();
const avisos = new Set<(e: Evento) => void>();
let ws: WebSocket | null = null;
let intentos = 0;
let reintento: ReturnType<typeof setTimeout> | null = null;
let ultimoEstado: { estado: EstadoPresencia; detalle: string } = { estado: 'disponible', detalle: '' };

const cambiar = (x: Partial<Estado>) => {
  estado = { ...estado, ...x };
  oyentes.forEach((f) => f());
};

function abrir() {
  const s = getSession();
  if (!s || s.modo !== 'usuario' || typeof WebSocket === 'undefined') return;
  const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/rt?token=${encodeURIComponent(s.accessToken)}`;
  const yoWs = new WebSocket(url);
  ws = yoWs;
  yoWs.onopen = () => {
    intentos = 0;
    cambiar({ conectado: true });
    yoWs.send(JSON.stringify({ tipo: 'estado', ...ultimoEstado }));
  };
  yoWs.onmessage = (m) => {
    let d: Evento;
    try {
      d = JSON.parse(String(m.data));
    } catch {
      return;
    }
    if (d.tipo === 'hola') cambiar({ yo: String((d as { miembroId?: string }).miembroId ?? '') });
    else if (d.tipo === 'presencia') cambiar({ presencia: ((d as { lista?: Presente[] }).lista ?? []) as Presente[] });
    else avisos.forEach((f) => f(d));
  };
  yoWs.onclose = () => {
    if (ws !== yoWs) return;
    ws = null;
    cambiar({ conectado: false });
    if (!getSession()) return;
    const espera = Math.min(30_000, 1000 * 2 ** Math.min(intentos++, 5)) + Math.random() * 1000;
    reintento = setTimeout(abrir, espera);
  };
}

export function conectarTiempoReal() {
  if (ws || reintento) return;
  abrir();
  onSessionChange(() => {
    if (reintento) clearTimeout(reintento);
    reintento = null;
    const viejo = ws;
    ws = null;
    viejo?.close();
    cambiar({ conectado: false, presencia: [] });
    if (getSession()) abrir();
  });
}

// D78: el siguiente estado que se anuncie vino de 3CX (no se le devuelve a 3CX como orden).
// Vale 3 s: si el estado no llega a anunciarse (p. ej. en llamada), no marca por error un cambio posterior.
let desde3cxEn = 0;
export const marcarDesde3cx = () => (desde3cxEn = Date.now());

export function anunciarEstado(e: EstadoPresencia, detalle = '') {
  if (ultimoEstado.estado === e && ultimoEstado.detalle === detalle) return;
  ultimoEstado = { estado: e, detalle };
  const desde3cx = Date.now() - desde3cxEn < 3000;
  desde3cxEn = 0;
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ tipo: 'estado', estado: e, detalle, desde3cx }));
}

export function avisarMensaje(id: string) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ tipo: 'chat', id }));
}

let ultimoEscribiendo = 0;
export function avisarEscribiendo(canal: string) {
  if (Date.now() - ultimoEscribiendo < 2500 || ws?.readyState !== WebSocket.OPEN) return;
  ultimoEscribiendo = Date.now();
  ws.send(JSON.stringify({ tipo: 'escribiendo', canal }));
}

export function alRecibir(f: (e: Evento) => void) {
  avisos.add(f);
  return () => {
    avisos.delete(f);
  };
}

// Para quien decide cada cuanto preguntar al servidor: con el tiempo real conectado, los avisos llegan solos.
export const tiempoRealConectado = () => estado.conectado;

export const useTiempoReal = () =>
  useSyncExternalStore(
    (f) => {
      oyentes.add(f);
      return () => {
        oyentes.delete(f);
      };
    },
    () => estado,
  );
